// Copyright (c) Docugami, Inc. All rights reserved.

import { LargeUI, dgFontFamily } from "components/common/Fonts"
import { greyText } from "components/shell/presentation/AppStyles"
import { RequestState, useAsyncHelper } from "hooks/AsyncHelper"
import { useKeyPressHandler } from "hooks/KeyPressHandler"
import { ReactNode, useEffect, useMemo, useState } from "react"
import styled, { css } from "styled-components"
import { DocSetAssignmentChooser } from "./DocSetChooser"
import { EntryBox } from "./EntryBox"
import { ConfirmationDialog } from "components/shell/utils/ConfirmationDialog"
import { IconRenderer } from "components/work_path/IconRenderer"
import { getIconInfoForWorkPath } from "components/work_path/WorkPathsHelper"
import { WorkPathType } from "networking/WorkPathApi"
import { dgIconNames } from "hooks/dgIcons"
import { OverflowTooltipText } from "components/common/OverflowText"
import { TooltipOverflowMode } from "@fluentui/react"
import { ReassignmentEffectItemType } from "store/frontend/frontendSlice"

const DisabledCss = css`
    .content {
        opacity: .5;
        pointer-events: none;
    }
`

const LoadingCss = css`
    ${DisabledCss};
    cursor: wait;
`

const Container = styled.div<{ disabled?: boolean, loading?: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    background: white;
    ${props => !!props.disabled && DisabledCss}
    ${props => !!props.loading && LoadingCss}
`

const ContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: center;
    align-items: center;
    height: 100%;
`

const TitleContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-top: 1px solid ${greyText};
    border-bottom: 1px solid ${greyText};
`

const TitleContentContainer = styled.div`
    margin: 5px 0px;
`

const ChooserContainer = styled.div`
    width: 40%;
    max-height: 80%;
`

const EntryBoxContainer = styled.div`
    width: 50%;
`

const ConfirmationDialogContent = styled.div`
    width: 465px;
    margin: auto;
    display: flex;
    flex-wrap: wrap;
`

const ConfirmationDialogContentItem = styled.div`
    flex: 50%;
    display: flex;
    justify-content: center;
`

enum AssignmentStep {
    Choose,
    Create
}

const chooseTitle = "Choose a doc set to assign these docs to or create a new one:";
const createTitle = "Give your new doc set a name:";

interface DocSetAssignmentProps {
    onChoose: (id?: string) => Promise<void>;
    onCreate: (label: string) => Promise<void>;
    onCancel: () => void;
    deletedItems?: AffectedItemProps[];
}

export const DocSetAssignment = (props: DocSetAssignmentProps): JSX.Element => {
    const [step, setStep] = useState(AssignmentStep.Choose);
    const [title, setTitle] = useState(chooseTitle);
    const { state: requestState, execute } = useAsyncHelper("DocSetAssignment action");
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [selectedDocSetId, setSelectedDocSetId] = useState<string | undefined>();
    const [desiredDocSetName, setDesiredDocSetName] = useState('');

    useKeyPressHandler('Escape', props.onCancel);

    function onChoose(id?: string) {
        if (!props.deletedItems?.length) {
            execute(props.onChoose(id));
        }
        else {
            setSelectedDocSetId(id);
            setShowConfirmation(true);
        }
    }

    function onCreate(name: string) {
        if (!props.deletedItems?.length) {
            // A new name has been confirmed
            execute(props.onCreate(name));
        } else {
            setDesiredDocSetName(name || '');
            setShowConfirmation(true);
        }
    }

    useEffect(() => {
        if (step === AssignmentStep.Choose) {
            setTitle(chooseTitle);
        }
        else if (step === AssignmentStep.Create) {
            setTitle(createTitle);
        }
    }, [step])

    const onConfirm = () => {
        if (step === AssignmentStep.Choose) {
            execute(props.onChoose(selectedDocSetId));
        }
        else {
            // Creating
            execute(props.onCreate(desiredDocSetName));
        }
        setShowConfirmation(false);
    }

    return (
        <DocSetExplorerChooserOverlay
            title={title}
            disabled={requestState === RequestState.InProgress}
            onDismiss={props.onCancel}
            overlays={(
                <ReassignmentWarningModal
                    hidden={!showConfirmation}
                    deletedItems={props.deletedItems}
                    onConfirm={onConfirm}
                    onCancel={props.onCancel} />)}>
            {
                step === AssignmentStep.Choose &&
                <ChooserContainer>
                    <DocSetAssignmentChooser onChoose={onChoose} onCreate={() => setStep(AssignmentStep.Create)} onCancel={props.onCancel} />
                </ChooserContainer>
            }
            {
                step === AssignmentStep.Create &&
                <EntryBoxContainer>
                    <EntryBox placeholder={"Example Doc Set"} disabled={false} onConfirm={onCreate} onCancel={props.onCancel} maxLength={50} />
                </EntryBoxContainer>
            }
        </DocSetExplorerChooserOverlay>
    )
}

interface DocSetExplorerChooserOverlayProps {
    title: ReactNode;
    disabled?: boolean;
    inProgress?: boolean;
    children?: ReactNode;
    onDismiss?: () => void;
    overlays?: ReactNode;
}

export const DocSetExplorerChooserOverlay = (props: DocSetExplorerChooserOverlayProps): JSX.Element => {
    useKeyPressHandler(!!props.onDismiss ? 'Escape' : undefined, () => props.onDismiss?.());

    return (
        <Container disabled={props.disabled} loading={props.inProgress}>
            <TitleContainer>
                <TitleContentContainer>
                    <LargeUI>{props.title}</LargeUI>
                </TitleContentContainer>
            </TitleContainer>
            <ContentContainer className="content">
                {props.children}
            </ContentContainer>
            {props.overlays}
        </Container>
    );
}

const AffectItemContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 10px;
    margin-top: 10px;
    height: 60px;
    max-width: 180px;
`

export interface AffectedItemProps {
    name: string;
    type: ReassignmentEffectItemType;
}

const AffectedItem = (props: AffectedItemProps) => {
    const iconDescriptor = useMemo(() => props.type === ReassignmentEffectItemType.WorkPath ?
        getIconInfoForWorkPath(WorkPathType.TabularReport, false, false)
        :
        {
            name: dgIconNames.DocsetFileIcon,
            height: 53,
            width: 45,
        }, [props.type]);

    return (
        <AffectItemContainer>
            <IconRenderer
                source={iconDescriptor}
                width={60}
                height={60} />
            <OverflowTooltipText
                content={props.name}
                tooltipOverflowMode={TooltipOverflowMode.Self}>
                {props.name}
            </OverflowTooltipText>
        </AffectItemContainer >
    )
}

interface ReassignmentWarningModalProps {
    hidden?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
    deletedItems?: AffectedItemProps[];
}

const ReassignmentWarningModal = (props: ReassignmentWarningModalProps): JSX.Element => {
    return (
        <ConfirmationDialog
            hidden={props.hidden}
            styles={{
                root: {
                    fontFamily: dgFontFamily,
                    '.ms-Dialog-subText': {
                        fontSize: 24,
                        lineHeight: 40,
                        fontweight: 400
                    }
                },
                main: {
                    textAlign: 'center',
                    maxHeight: 580,
                    width: '56% !important',
                    maxWidth: '56% !important',
                    minWidth: '56% !important'
                },
            }}
            title={"ARE YOU SURE YOU WANT TO REASSIGN?"}
            subText={"Reassigning these documents will cause the following to be deleted. You can not undo this action."}
            confirmButtonText={"YES, CONTINUE"}
            dismissButtonText={"NO, CANCEL"}
            dismissedCallback={props.onCancel}
            confirmedCallback={props.onConfirm} >
            <ConfirmationDialogContent>
                {props.deletedItems?.map((item, index) =>
                    <ConfirmationDialogContentItem key={item.name}>
                        <AffectedItem {...item} />
                    </ConfirmationDialogContentItem>
                )}
            </ConfirmationDialogContent>
        </ConfirmationDialog>
    )
}
